<template>
	<div class="competency-chart-card">
		<div class="competency-chart-card__header">
			<h4 v-if="dataCompetences.type !== 'radar'" class="competency-chart-card__title">
				{{ truncateText(title, 35) }}
			</h4>
			<p v-else class="competency-chart-card__title competency-chart-card__title--small">
				{{ truncateText(title, 25) }}
			</p>
			<button
				v-if="!view"
				class="btn-view-chart"
				@click="$emit('show-graphic', dataCompetences)"
				aria-label="Ver gráfico expandido"
			>
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
				</svg>
			</button>
		</div>
		<div class="competency-chart-card__body">
			<apexchart
				:width="'100%'"
				:height="height"
				:type="chart.type"
				:options="modernChartOptions"
				:series="chart.series"
			></apexchart>
		</div>
	</div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import ChartDataBuilder from '@/shared/chart-builder';

const chartBuilder = new ChartDataBuilder();

function created() {
	chartBuilder.type = this.dataCompetences.type;
	chartBuilder.series = this.dataCompetences.series;
	chartBuilder.categories = this.dataCompetences.categories;
	this.chart = chartBuilder.generatechartData();
}

function data() {
	return {
		chart: {},
	};
}

export default {
	name: 'card-dashboard',
	created,
	data,
	components: {
		apexchart: VueApexCharts,
	},
	props: {
		dataCompetences: {
			type: Object,
			default() {
				return {};
			},
		},
		title: {
			type: String,
			default: '',
		},
		height: {
			type: [Number, String],
		},
		width: {
			type: [Number, String],
		},
		view: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		modernChartOptions() {
			const baseOptions = this.chart.options || {};
			const chartType = this.chart.type || this.dataCompetences.type;

			// Solo aplicar mejoras cosméticas, preservar colores y datos originales
			const cosmeticStyles = {
				fontFamily: 'inherit',
				toolbar: { show: false },
			};

			// Construir options preservando todo lo original
			const enhancedOptions = {
				chart: {
					...(baseOptions.chart || {}),
					...cosmeticStyles,
					height: '100%',
				},
			};

			// Agregar grid si no existe
			if (!baseOptions.grid) {
				enhancedOptions.grid = {
					show: true,
					borderColor: '#f1f5f9',
					strokeDashArray: 0,
					padding: {
						top: 15,
						right: 50,
						bottom: 15,
						left: 15,
					},
				};
			} else {
				enhancedOptions.grid = baseOptions.grid;
				// Agregar padding si no existe en el grid original
				if (!enhancedOptions.grid.padding) {
					enhancedOptions.grid.padding = {
						top: 15,
						right: 50,
						bottom: 15,
						left: 15,
					};
				} else {
					// Asegurar padding mínimo en el grid original
					if (!enhancedOptions.grid.padding.top || enhancedOptions.grid.padding.top < 15) {
						enhancedOptions.grid.padding.top = 15;
					}
					if (!enhancedOptions.grid.padding.right || enhancedOptions.grid.padding.right < 50) {
						enhancedOptions.grid.padding.right = 50;
					}
					if (!enhancedOptions.grid.padding.bottom || enhancedOptions.grid.padding.bottom < 15) {
						enhancedOptions.grid.padding.bottom = 15;
					}
					if (!enhancedOptions.grid.padding.left || enhancedOptions.grid.padding.left < 15) {
						enhancedOptions.grid.padding.left = 15;
					}
				}
			}

			// Mejorar tooltip si existe
			if (baseOptions.tooltip) {
				enhancedOptions.tooltip = {
					...baseOptions.tooltip,
					theme: 'light',
					style: {
						fontSize: '12px',
						fontFamily: 'inherit',
					},
				};
			} else {
				enhancedOptions.tooltip = {
					enabled: true,
					theme: 'light',
					style: {
						fontSize: '12px',
						fontFamily: 'inherit',
					},
				};
			}

			// Copiar todas las demás opciones originales
			const keysToCopy = ['plotOptions', 'dataLabels', 'xaxis', 'yaxis', 'stroke', 'fill', 'colors', 'labels', 'legend', 'dataProvider'];
			keysToCopy.forEach((key) => {
				if (baseOptions[key] !== undefined) {
					enhancedOptions[key] = baseOptions[key];
				}
			});

			// Aplicar estilos modernos específicos por tipo SOLO si no existen en el original
			switch (chartType) {
			case 'bar':
				// Preservar borderRadius original si existe
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.bar) {
					enhancedOptions.plotOptions.bar = {};
				}
				// Solo aplicar borderRadius si el original tiene 0
				if (!enhancedOptions.plotOptions.bar.borderRadius
					|| enhancedOptions.plotOptions.bar.borderRadius === 0) {
					enhancedOptions.plotOptions.bar.borderRadius = 8;
					enhancedOptions.plotOptions.bar.borderRadiusApplication = 'end';
				}
				// Preservar isFunnel del original
				if (baseOptions.plotOptions
					&& baseOptions.plotOptions.bar
					&& baseOptions.plotOptions.bar.isFunnel) {
					enhancedOptions.plotOptions.bar.isFunnel = true;
				}
				// Mejorar estilos de labels si existen
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '8px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (enhancedOptions.yaxis
					&& enhancedOptions.yaxis.labels
					&& !enhancedOptions.yaxis.labels.style) {
					enhancedOptions.yaxis.labels = {
						...enhancedOptions.yaxis.labels,
						style: {
							fontSize: '8px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				// Mejorar dataLabels style y formatter
				if (enhancedOptions.dataLabels
					&& enhancedOptions.dataLabels.enabled) {
					if (!enhancedOptions.dataLabels.style) {
						enhancedOptions.dataLabels.style = {
							fontSize: '8px',
							fontWeight: 600,
							colors: ['#334155'],
						};
					}
					// Usar formatter más corto que solo muestre el valor
					if (!enhancedOptions.dataLabels.formatter) {
						enhancedOptions.dataLabels.formatter = function formatterVal(val) {
							return val;
						};
					}
				}
				break;

			case 'singlebar':
				// Preservar configuración original específica de singlebar
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.bar) {
					enhancedOptions.plotOptions.bar = {};
				}
				// Solo aplicar borderRadius si el original tiene 0
				if (!enhancedOptions.plotOptions.bar.borderRadius
					|| enhancedOptions.plotOptions.bar.borderRadius === 0) {
					enhancedOptions.plotOptions.bar.borderRadius = 8;
					enhancedOptions.plotOptions.bar.borderRadiusApplication = 'end';
				}
				// Mejorar estilos de labels
				if (enhancedOptions.yaxis
					&& enhancedOptions.yaxis.labels
					&& !enhancedOptions.yaxis.labels.style) {
					enhancedOptions.yaxis.labels.style = {
						fontSize: '9px',
						fontWeight: 600,
						colors: ['#334155'],
					};
				}
				break;

			case 'area':
				// Mejorar stroke si existe
				if (enhancedOptions.stroke) {
					if (!enhancedOptions.stroke.width) {
						enhancedOptions.stroke.width = 3;
					}
				}
				// Agregar fill gradiente si no existe
				if (!enhancedOptions.fill) {
					enhancedOptions.fill = {
						type: 'gradient',
						gradient: {
							shadeIntensity: 1,
							opacityFrom: 0.4,
							opacityTo: 0.05,
							stops: [0, 90, 100],
						},
					};
				}
				// Mejorar estilos de labels
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (baseOptions.yaxis
					&& baseOptions.yaxis.labels
					&& !baseOptions.yaxis.labels.style) {
					enhancedOptions.yaxis = baseOptions.yaxis;
					enhancedOptions.yaxis.labels = {
						...baseOptions.yaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				} else if (!baseOptions.yaxis) {
					enhancedOptions.yaxis = {
						labels: {
							style: {
								fontSize: '10px',
								fontWeight: 500,
								colors: ['#64748b'],
							},
						},
					};
				}
				break;

			case 'line':
				// Mejorar stroke si existe
				if (enhancedOptions.stroke) {
					if (!enhancedOptions.stroke.width) {
						enhancedOptions.stroke.width = 3;
					}
					if (!enhancedOptions.stroke.curve) {
						enhancedOptions.stroke.curve = 'smooth';
					}
				}
				// Agregar markers si no existen
				if (!enhancedOptions.markers) {
					enhancedOptions.markers = {
						size: 4,
						strokeWidth: 0,
						hover: { size: 6 },
					};
				}
				// Mejorar estilos de labels
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (baseOptions.yaxis
					&& baseOptions.yaxis.labels
					&& !baseOptions.yaxis.labels.style) {
					enhancedOptions.yaxis = baseOptions.yaxis;
					enhancedOptions.yaxis.labels = {
						...baseOptions.yaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				} else if (!baseOptions.yaxis) {
					enhancedOptions.yaxis = {
						labels: {
							style: {
								fontSize: '10px',
								fontWeight: 500,
								colors: ['#64748b'],
							},
						},
					};
				}
				break;

			case 'radar':
				// Para radar, preservar configuración original y solo mejorar estilos
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.radialBar) {
					enhancedOptions.plotOptions.radialBar = {};
				}
				if (!enhancedOptions.plotOptions.radialBar.hollow) {
					enhancedOptions.plotOptions.radialBar.hollow = {
						size: '55%',
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.track) {
					enhancedOptions.plotOptions.radialBar.track = {
						background: '#f1f5f9',
						strokeWidth: '97%',
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels) {
					enhancedOptions.plotOptions.radialBar.dataLabels = {};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels.name) {
					enhancedOptions.plotOptions.radialBar.dataLabels.name = {
						show: true,
						fontSize: '10px',
						color: '#64748b',
						fontWeight: 600,
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels.value
					|| !enhancedOptions.plotOptions.radialBar.dataLabels.value.fontSize) {
					enhancedOptions.plotOptions.radialBar.dataLabels.value = {
						fontSize: '14px',
						fontWeight: 700,
						color: '#0f172a',
						formatter: function formatterVal(val) {
							return `${Number(val).toFixed(1)}%`;
						},
					};
				}
				// Agregar legend si no existe
				if (!enhancedOptions.legend) {
					enhancedOptions.legend = {
						show: true,
						position: 'bottom',
						fontSize: '9px',
						fontWeight: 600,
						itemMargin: {
							horizontal: 5,
							vertical: 2,
						},
					};
				}
				break;

			default:
				break;
			}

			// Ajustar leyendas para evitar superposición con labels
			if (enhancedOptions.legend) {
				// Si la leyenda existe, asegurarnos de que tenga buen posicionamiento
				if (!enhancedOptions.legend.offsetY) {
					enhancedOptions.legend.offsetY = 0;
				}
				if (!enhancedOptions.legend.offsetX) {
					enhancedOptions.legend.offsetX = 0;
				}
				// Si la leyenda está en bottom, aumentar padding inferior
				if (enhancedOptions.legend.position === 'bottom'
					|| !enhancedOptions.legend.position) {
					// Aumentar padding inferior del grid para dar espacio a la leyenda
					if (!enhancedOptions.grid.padding) {
						enhancedOptions.grid.padding = {};
					}
					if (!enhancedOptions.grid.padding.bottom || enhancedOptions.grid.padding.bottom < 20) {
						enhancedOptions.grid.padding.bottom = 25;
					}
				}
				// Si la leyenda está en top, aumentar padding superior
				if (enhancedOptions.legend.position === 'top') {
					if (!enhancedOptions.grid.padding) {
						enhancedOptions.grid.padding = {};
					}
					if (!enhancedOptions.grid.padding.top || enhancedOptions.grid.padding.top < 15) {
						enhancedOptions.grid.padding.top = 20;
					}
				}
			}

			return enhancedOptions;
		},
	},
	methods: {
		truncateText(text, maxLength) {
			if (text.length > maxLength) {
				return `${text.substring(0, maxLength)}...`;
			}
			return text;
		},
	},
};
</script>

<style lang="scss" scoped>
.competency-chart-card {
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	transition: box-shadow 0.3s ease, transform 0.3s ease;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1rem 1.25rem;
		border-bottom: 1px solid #f1f5f9;
		background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	}

	&__title {
		font-size: 0.875rem;
		font-weight: 700;
		color: #0f172a;
		margin: 0;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		line-height: 1.3;

		&--small {
			font-size: 0.75rem;
		}
	}

	&__body {
		padding: 1rem;
		min-height: 200px;
		width: 100%;
		height: 100%;

		.apexcharts-canvas {
			width: 100% !important;
		}
	}
}

.btn-view-chart {
	background: transparent;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #64748b;
	padding: 0;

	&:hover {
		background: #f1f5f9;
		border-color: #cbd5e1;
		color: #0f172a;
	}

	&:focus {
		outline: 2px solid #3b82f6;
		outline-offset: 2px;
	}

	svg {
		width: 16px;
		height: 16px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/card-dashboard.vue